import { useQuery } from '@tanstack/react-query'
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { adminApi } from '@/api/admin'
import { KpiCard, EmptyChart, SectionTitle } from './AnalyticsShared'
import { useAnalyticsMoney } from './analyticsHelpers'

export function OverviewTab({ from, to }: { from: string | null; to: string | null }) {
  const { fmtMoney } = useAnalyticsMoney()
  const { data, isLoading } = useQuery({
    queryKey: ['analytics-overview', from, to],
    queryFn: () => adminApi.getAnalyticsOverview(from, to).then(r => r.data),
  })

  if (isLoading) return <p className="text-gray-400 text-sm py-8">Loading…</p>
  if (!data) return null

  const completionRate = data.totalBookings > 0
    ? Math.round((data.completedBookings / data.totalBookings) * 100)
    : 0

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KpiCard label="Total revenue" value={fmtMoney(data.totalRevenue)} accent />
        <KpiCard label="Outstanding" value={fmtMoney(data.outstandingAmount)}
          sub={data.overdueInvoices > 0 ? `${data.overdueInvoices} overdue` : undefined} />
        <KpiCard label="Total bookings" value={data.totalBookings.toString()}
          sub={`${completionRate}% completed`} />
        <KpiCard label="Cancelled / Rejected" value={data.cancelledBookings.toString()} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KpiCard label="Certificates issued" value={data.totalCertificates.toString()} />
        <KpiCard label="Avg rating"
          value={data.averageRating != null ? `${data.averageRating.toFixed(1)}/10` : '—'}
          sub={data.totalFeedback > 0 ? `${data.totalFeedback} reviews` : 'No reviews'} />
        <KpiCard label="Overdue invoices" value={data.overdueInvoices.toString()} />
        {data.topClientName && (
          <KpiCard label="Top client" value={data.topClientName}
            sub={data.topClientRevenue != null ? fmtMoney(data.topClientRevenue) : undefined} />
        )}
      </div>

      {data.live && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <KpiCard label="Live sessions" value={data.live.sessions.toString()} sub={`${data.live.participants} participants`} />
          <KpiCard label="Attendee feedback (Live)"
            value={data.live.feedbackAverage != null ? `${data.live.feedbackAverage.toFixed(1)}/10` : '—'}
            sub={`${data.live.feedbackResponses} response${data.live.feedbackResponses === 1 ? '' : 's'}`} />
          <KpiCard label="Avg learning gain"
            value={data.live.learningGain != null ? `${data.live.learningGain >= 0 ? '+' : ''}${data.live.learningGain.toFixed(1)} pts` : '—'}
            sub="Post-course vs pre-course" />
          <KpiCard label="Avg confidence change"
            value={data.live.confidenceChange != null ? `${data.live.confidenceChange >= 0 ? '+' : ''}${data.live.confidenceChange.toFixed(1)}` : '—'}
            sub="Out of 10" />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <SectionTitle>Booking status breakdown</SectionTitle>
          <div className="mt-4 h-48">
            {data.totalBookings === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Completed', value: data.completedBookings },
                      { name: 'Cancelled', value: data.cancelledBookings },
                      { name: 'Active',    value: Math.max(0, data.totalBookings - data.completedBookings - data.cancelledBookings) },
                    ].filter(d => d.value > 0)}
                    cx="50%" cy="50%"
                    innerRadius={55} outerRadius={80}
                    paddingAngle={3} dataKey="value"
                  >
                    {[0, 1, 2].map(i => (
                      <Cell key={i} fill={['#6366f1', '#e5e7eb', '#f59e0b'][i]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => [Number(v), '']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <SectionTitle>At a glance</SectionTitle>
          <div className="mt-4 space-y-3">
            {[
              { label: 'Completed bookings',  value: `${data.completedBookings} / ${data.totalBookings}`, bar: completionRate },
              { label: 'Invoices outstanding', value: fmtMoney(data.outstandingAmount), bar: null },
              { label: 'Avg client rating',    value: data.averageRating != null ? `${data.averageRating.toFixed(1)}/10` : '—', bar: data.averageRating ? data.averageRating * 10 : null },
              { label: 'Certs issued',         value: data.totalCertificates.toString(), bar: null },
            ].map(row => (
              <div key={row.label}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">{row.label}</span>
                  <span className="font-semibold text-gray-900">{row.value}</span>
                </div>
                {row.bar != null && (
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${Math.min(100, row.bar)}%` }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
